// Utility helpers to detect the current company from URL (dynamic, no whitelist)
export type Company = string;

function firstHostLabel(hostname: string): string | undefined {
  const parts = hostname.toLowerCase().split(".");
  if (parts.length === 0) return undefined;
  return parts[0];
}

export function isCompanySubdomain(): boolean {
  try {
    const host = window.location.hostname.toLowerCase();
    // Only treat subdomains under public roots as tenants
    const allowedRoot = host.endsWith('.talenty.ai') || host.endsWith('.nip.io');
    if (!allowedRoot) return false;
    const first = firstHostLabel(host);
    // Consider any non-empty first label that's not 'www' as a tenant subdomain
    return !!first && first !== 'www';
  } catch {
    return false;
  }
}

export function getCompanyFromUrl(): Company {
  // 1) Prefer subdomain
  if (isCompanySubdomain()) {
    return firstHostLabel(window.location.hostname) as Company;
  }

  // 2) Fallback to the first path segment (avoid reserved system prefixes)
  const reserved = new Set([
    'assets', 'api', 'homepage', 'legal', 'job', 'jobs', 'static', 'favicon.ico',
    'domain', 'domains', 'family', 'families', 'faq', 'impressum',
    'datenschutz', 'datenschutz-bewerber', 'project', 'projects', 'ueber-uns'
  ]);
  const pathSegments = window.location.pathname
    .split('/')
    .filter((s) => s.length > 0);
  const firstSegment = pathSegments[0]?.toLowerCase();
  if (firstSegment && !reserved.has(firstSegment)) {
    return firstSegment as Company;
  }

  // 3) Default
  return 'dwg';
}

export function isJobPagePath(pathname: string): boolean {
  return /^\/(?:[^/]+\/)?job\//.test(pathname);
}

export function isCareerPagePath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return path === '/' || path === `/${getCompanyFromUrl()}` ||
    /^\/(?:[^/]+\/)?(?:jobs$|project\/|job\/)/.test(path);
}

export function companyPath(page = ''): string {
  return isCompanySubdomain() ? `/${page}` : `/${getCompanyFromUrl()}/${page}`;
}

export function getCompanyBranding() {
  switch (getCompanyFromUrl()) {
    case 'talentschmiede':
      return { name: 'Talentschmiede', legalName: 'Talentschmiede AG', logo: '/talentschmiedeLogo.png' };
    case 'digitaldavid':
      return { name: 'Digital David', legalName: 'Digital David AG', logo: '/digitaldavidlogo.png' };
    default:
      return { name: 'Digital Workforce Group', legalName: 'The Digital Workforce Group AG', logo: '/logo-long.png' };
  }
}
